import { SearchOutlined } from "@ant-design/icons";
import { message } from "antd";
import { Form, Formik } from "formik";
import React, { useMemo, useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import * as Yup from "yup";
import { useFiltersAndDates } from "../../../hooks/useFiltersAndDate";
import { useSelector } from "../../../data/store";
import {
  importOFXFile,
  clearErrors,
} from "../../../data/slices/importedElements";
import Button from "../../Button";
import CustomDropdown from "../../CustomSelector";
import Overlay from "../../Overlay";
import HeaderOverlay from "../../Project/Header";
import Title from "../../Project/Title";
import "./index.scss";

function ImportOFXModal({ open, onCancel, data, ...rest }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const formikRef = useRef(null);

  // Get accounts from Redux store
  const { accounts } = useSelector((state) => state.accounts);

  // Get import status from imported elements slice with defensive checks
  const importedElementsState = useSelector(
    (state) => state.importedElements || {}
  );
  const {
    importStatus = "idle",
    isLoading = false,
  } = importedElementsState;
  // Form validation schema
  const ImportSchema = Yup.object().shape({
    accountTresoId: Yup.string().required(
      "Veuillez sélectionner un compte de trésorerie"
    ),
    file: Yup.mixed().required("Veuillez sélectionner un fichier OFX"),
  });

  const principalAccount = accounts.find((el) => el?.type === "PRINCIPAL");

  // Initial form values
  const initialValues = useMemo(
    () => ({
      accountColor: principalAccount?.color,
      accountTresoId: principalAccount?.id,
      accountTresoLabel: principalAccount?.name,
      accountCode: principalAccount?.code,
      file: null,
    }),
    [principalAccount]
  );

  useEffect(() => {
    if (open) {
      dispatch(clearErrors());
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setSelectedFile(null);
      if (formikRef.current) {
        formikRef.current.resetForm();
      }
    }
  }, [open, dispatch]);

  const handleFileChange = (event, setFieldValue) => {
    const file = event.target.files[0];

    if (file) {
      if (!file.name.toLowerCase().endsWith(".ofx")) {
        message.error(`${file.name} n'est pas un fichier OFX valide`);
        setSelectedFile(null);
        setFieldValue("file", null);
        return;
      }

      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error("Le fichier doit être inférieur à 10MB!");
        setSelectedFile(null);
        setFieldValue("file", null);
        return;
      }

      setSelectedFile(file);
      setFieldValue("file", file);
    } else {
      setSelectedFile(null);
      setFieldValue("file", null);
    }
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const location = useLocation();
  const { constructFilters, getStartAndEndDate, getStartAndEndDateSynthesis } =
    useFiltersAndDates();

  const { footerConstructedFilters, tableConstructedFilters } =
    constructFilters();

  const { startDate, endDate } = getStartAndEndDate();

  const { startDateSynthesis, endDateSynthesis } =
    getStartAndEndDateSynthesis();

  const { objectif } = useSelector((state) => state.objectives);

  const paramsValues = {
    isListing: location.pathname.includes("listing"),
    listing: {
      params: { ...tableConstructedFilters, startDate, endDate },
      mode: "treso",
      projectId: data?.projectId,
    },
    sum: {
      mode: "treso",
      projectId: data?.projectId,
      params: {
        startDate,
        endDate,
        ...footerConstructedFilters,
      },
    },
    synthesis: {
      mode: "treso",
      projectId: data?.projectId,
      params: {
        startDate: startDateSynthesis,
        endDate: endDateSynthesis,
        ...tableConstructedFilters,
      },
    },
    objective: {
      objectiveId: objectif?.id,
      isPlancompta: false,
    },
  };

  const handleFormSubmit = async (values, { setSubmitting, resetForm }) => {
    if (!values.file || !values.accountTresoId) {
      message.error("Veuillez remplir tous les champs requis");
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("file", values.file.originFileObj || values.file);
      formData.append("accountTresoId", Number(values.accountTresoId));
      formData.append("projectId", data.projectId);

      // Dispatch the Redux action
      const result = await dispatch(
        importOFXFile({
          formData,
          projectId: data.projectId,
          values: paramsValues,
        })
      ).unwrap();

      message.success("Fichier OFX importé avec succès");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setSelectedFile(null);
      resetForm();

      if (typeof onCancel === "function") {
        onCancel();
      }
      if (data?.onSuccess) {
        data.onSuccess(result);
      }
    } catch (error) {
      const errorMessage =
        error?.message ||
        error ||
        "Erreur lors de l'importation du fichier OFX";
      message.error(errorMessage);
      console.error("Import error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setSelectedFile(null);
    if (formikRef.current) {
      formikRef.current.resetForm();
    }
    if (typeof onCancel === "function") {
      onCancel();
    }
  };

  // Check if we're currently uploading
  const uploading = isLoading || importStatus === "loading";

  return (
    <Overlay show={open} closable={true}>
      <HeaderOverlay showlogo>
        <Title isDelete subTitle={`Importer un fichier OFX`} />

        <Formik
          key={open ? "open" : "closed"}
          ref={formikRef}
          initialValues={initialValues}
          validationSchema={ImportSchema}
          onSubmit={handleFormSubmit}
          validateOnChange={false}
          validateOnBlur={false}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            handleBlur,
            setFieldValue,
            handleSubmit,
          }) => {
            return (
              <Form className="import-ofx-form" onSubmit={handleSubmit}>
                <div className="import-ofx-content">
                  <div className="import-ofx-content-label">
                    Vous allez importer des écritures via un fichier OFX.
                    Veuillez sélectionner votre fichier :
                  </div>

                  <div className="file-input-section-wrapper">
                    <div className="file-input-section">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".ofx"
                        style={{ display: "none" }}
                        onChange={(event) =>
                          handleFileChange(event, setFieldValue)
                        }
                      />
                      <div
                        className="file-input-button"
                        onClick={handleFileButtonClick}
                      >
                        <SearchOutlined className="search-icon" />
                        <span className="file-input-text">
                          Rechercher un fichier
                        </span>
                      </div>
                      {errors.file && touched.file && (
                        <div className="error-message">{errors.file}</div>
                      )}
                      {selectedFile && (
                        <div className="selected-file-name">
                          {selectedFile.name}
                        </div>
                      )}
                    </div>

                    <div className="import-ofx-content-label">
                      Sélectionner le compte de trésorerie sur lequel importer
                      les écritures :
                    </div>

                    <CustomDropdown
                      onBlur={handleBlur}
                      onChange={setFieldValue}
                      values={values}
                      projectId={data?.projectId}
                      disabled={uploading}
                    />

                    {errors.accountTresoId && touched.accountTresoId && (
                      <div className="error-message">
                        {errors.accountTresoId}
                      </div>
                    )}
                  </div>

                  <div className="verify-inputs-popup">
                    <Button
                      text={"Annuler"}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleCancel();
                      }}
                      className={"action-button"}
                      htmlType="button"
                      disabled={isSubmitting || uploading}
                    />
                    <Button
                      text={"Valider"}
                      htmlType="submit"
                      className={"action-button agree-action-button"}
                      iconClassName={"delete-icon"}
                      disabled={isSubmitting || uploading}
                      loading={isSubmitting || uploading}
                    />
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </HeaderOverlay>
    </Overlay>
  );
}

export default ImportOFXModal;
